import { useState } from "react";
import { useUpdateField } from "@/hooks/use-update-field";
import { formatDate } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";

interface EditableInfoRowProps {
  label: string;
  value: string | null | undefined;
  field: string;
  projectId: string;
  /** VIB-757: project.updated_at brukes til optimistic locking. */
  projectUpdatedAt: string;
  type?: "text" | "date" | "number" | "select";
  options?: { value: string; label: string }[];
  /**
   * Når false: select-en viser ikke det tomme «—»-valget. Brukes for
   * NOT NULL-felter (f.eks. project_type) der null ikke er gyldig. Default true.
   */
  allowEmpty?: boolean;
  suffix?: string;
  /**
   * Når true: feltet er kun visning, klikk åpner ikke editor.
   * VIB-722c: brukes til å gates redigering med project_role_permissions.
   */
  readOnly?: boolean;
  /**
   * Når true: tom verdi kan ikke lagres (NOT NULL-felter, f.eks. prosjektnavn).
   * Lagring blokkeres og editoren forblir åpen til en verdi er fylt inn.
   */
  required?: boolean;
}

export function EditableInfoRow({
  label,
  value,
  field,
  projectId,
  projectUpdatedAt,
  type = "text",
  options = [],
  allowEmpty = true,
  suffix = "",
  readOnly = false,
  required = false,
}: EditableInfoRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value ?? "");
  const updateField = useUpdateField();
  const isSaving = updateField.isPending;

  const displayValue = value ?? "—";
  const formattedValue = type === "date" && value
    ? formatDate(value)
    : type === "select" && value
      ? (options.find(o => o.value === value)?.label ?? displayValue)
      : displayValue;

  const handleSave = () => {
    if (editValue === (value ?? "")) {
      setIsEditing(false);
      return;
    }

    // NOT NULL-felter (required): blokker tom verdi, hold editoren åpen.
    if (required && editValue.trim() === "") {
      return;
    }

    let nextValue: unknown;
    if (type === "number") {
      nextValue = editValue === "" ? null : parseFloat(editValue);
    } else {
      nextValue = editValue === "" ? null : editValue;
    }

    updateField.mutate(
      {
        table: "projects",
        id: projectId,
        field,
        value: nextValue,
        expectedUpdatedAt: projectUpdatedAt,
      },
      {
        onSuccess: () => setIsEditing(false),
        onError: () => setEditValue(value ?? ""),
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setIsEditing(false);
      setEditValue(value ?? "");
    }
  };

  if (!isEditing) {
    if (readOnly) {
      return (
        <div className="flex justify-between items-center gap-2 px-2 py-1">
          {label && <span className="text-muted-foreground whitespace-nowrap">{label}</span>}
          <span className="font-medium text-right">
            {formattedValue}
            {suffix && <span className="text-muted-foreground">{suffix}</span>}
          </span>
        </div>
      );
    }
    return (
      <div
        className="flex justify-between items-center gap-2 group cursor-pointer hover:bg-muted/40 px-2 py-1 rounded transition-colors"
        onClick={() => setIsEditing(true)}
      >
        {label && <span className="text-muted-foreground whitespace-nowrap">{label}</span>}
        <div className="flex items-center gap-2">
          <span className="font-medium text-right">
            {formattedValue}
            {suffix && <span className="text-muted-foreground">{suffix}</span>}
          </span>
          <SrIcon name="pencil" className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"  />
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-between items-center gap-2 bg-muted/50 px-2 py-1 rounded">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1">
        {type === "select" ? (
          <select
            value={editValue}
            onChange={(e) => {
              const newVal = e.target.value;
              setEditValue(newVal);
              if (newVal === (value ?? "")) { setIsEditing(false); return; }
              const parsedVal = newVal === "" ? null : /^\d+$/.test(newVal) ? parseInt(newVal, 10) : newVal;
              updateField.mutate(
                {
                  table: "projects",
                  id: projectId,
                  field,
                  value: parsedVal,
                  expectedUpdatedAt: projectUpdatedAt,
                },
                {
                  onSuccess: () => setIsEditing(false),
                  onError: () => {
                    setEditValue(value ?? "");
                    setIsEditing(false);
                  },
                },
              );
            }}
            onKeyDown={(e) => { if (e.key === "Escape") { setIsEditing(false); setEditValue(value ?? ""); } }}
            autoFocus
            className="px-2 py-1 text-sm border rounded bg-background"
          >
            {allowEmpty && <option value="">—</option>}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            autoFocus
            className="px-2 py-1 text-sm border rounded bg-background w-32"
          />
        )}
        {isSaving && <SrIcon name="loader2" className="w-3.5 h-3.5 animate-spin text-muted-foreground"  />}
      </div>
    </div>
  );
}

export default EditableInfoRow;
